/*
 * Schema Kanban Board
 *
 * Features:
 * - Displays schema-backed account or contact lanes with filtering and drag updates
 * - Keeps account/contact Query caches coherent after lane mutations
 *
 * Changes:
 * - 2026-08-06: Routed entity writes through the canonical Query mutation hooks
 */

import { useEffect, useState, useMemo, useImperativeHandle, forwardRef } from 'react';
import { toast } from 'react-toastify';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { type Schema } from '../../context/SchemaContext';
import { type FormField } from '../common/FormField';
import { request } from '../../services/dataService';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import KanbanColumnComponent from './KanbanColumn';
import Spinner from '../common/Spinner';
import { type FilterGroup } from '../../models/kanban';
import { applyFilters } from '../../utils/kanbanFilters';
import { useTeams } from '../../context/TeamContext';
import { useUpdateAccountMutation } from '../../query/accountQueries';
import { useUpdateContactMutation } from '../../query/contactQueries';

const RLP_TEAM_ID = -1; // Special teamId value for RLP legacy records

interface KanbanBoardProps {
  entityType: number; // 1 for contacts, 2 for accounts
  schema: Schema;
  field: FormField;
  searchQuery?: string;
  visibleLanes?: string[];      // Array of visible lane values (empty = show all)
  includeUnassigned?: boolean;  // Show unassigned lane
  filters?: FilterGroup;        // Filter configuration
  boardName?: string;           // Name of the kanban board
}

interface KanbanItem {
  id: string;
  title: string;
  subtitle?: string;
  data: any;
  columnId: string;
}

interface KanbanColumn {
  id: string;
  title: string;
  items: KanbanItem[];
}

export interface KanbanBoardHandle {
  exportBoard: () => void;
}

const KanbanBoard = forwardRef<KanbanBoardHandle, KanbanBoardProps>(
  (
    {
      entityType,
      schema,
      field,
      searchQuery = '',
      visibleLanes = [],
      includeUnassigned = true,
      filters,
      boardName
    },
    ref
  ) => {
    const { currentUserId } = useTeams();
    const updateAccount = useUpdateAccountMutation();
    const updateContact = useUpdateContactMutation();
    const currentUserIdStr = currentUserId?.toString() || '';

    const [columns, setColumns] = useState<KanbanColumn[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rawData, setRawData] = useState<any[]>([]); // Store raw data separately
    const [draggingId, setDraggingId] = useState<string | null>(null);
    const [hoveredColumnId, setHoveredColumnId] = useState<string | null>(null);

    // Apply filters to raw data
    const filteredData = useMemo(() => {
      if (!filters || (filters.rules.length === 0 && filters.groups.length === 0)) {
        return rawData;
      }
      return applyFilters(rawData, filters, currentUserIdStr);
    }, [rawData, filters, currentUserIdStr]);

    // Create columns based on field options
    const createColumns = (fieldOptions: string[], data: any[]) => {
      // Always include an "Unassigned" column for items without a value
      const columnIds = ['unassigned', ...fieldOptions];

      return columnIds.map(optionId => {
        // Get display title - for objects, try to find the label
        let displayTitle = optionId;
        if (optionId === 'unassigned') {
          displayTitle = 'Unassigned';
        } else {
          // Ensure optionId is a string
          displayTitle = String(optionId);

          // Find the original option to get the proper label
          const originalOption = field.options?.find(opt => {
            if (typeof opt === 'string') return opt === optionId;
            if (typeof opt === 'object' && opt !== null) {
              return String(opt.value || opt.label || opt) === optionId;
            }
            return String(opt) === optionId;
          });

          if (originalOption && typeof originalOption === 'object' && originalOption.label) {
            displayTitle = String(originalOption.label);
          }
        }

        return {
          id: String(optionId),
          title: displayTitle,
          items: data
            .filter(item => {
              const fieldValue = item[field.name] || '';
              const matchesColumn = optionId === 'unassigned'
                ? !fieldValue || fieldValue === ''
                : fieldValue === optionId;

              // If no search query, just return column match
              if (!searchQuery) return matchesColumn;

              // Apply search filtering
              const matchesSearch = entityType === 2
                ? // For accounts, search in name and industry
                (item.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (item.industry || '').toLowerCase().includes(searchQuery.toLowerCase())
                : // For contacts, search in displayName, firstName, lastName, email, and title
                (item.displayName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (item.firstName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (item.lastName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (item.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (item.title || '').toLowerCase().includes(searchQuery.toLowerCase());

              return matchesColumn && matchesSearch;
            })
            .map(item => ({
              id: item.id.toString(),
              title: entityType === 2
                ? (item.name || 'Unnamed Account')
                : (item.displayName || `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'Unnamed Contact'),
              subtitle: entityType === 2 ? item.industry : item.email,
              data: item,
              columnId: optionId
            }))
        };
      });
    };

    // Fetch data based on entity type (only when entity type, schema, or field changes)
    useEffect(() => {
      const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
          // Determine API endpoint and parameters based on entity type
          let endpoint: string;
          const teamId = schema.rlp ? RLP_TEAM_ID : schema.teamId;

          if (entityType === 2) {
            // Accounts - use team-filtered endpoint
            endpoint = API_ENDPOINTS.GET_TEAM_ACCOUNTS;
          } else {
            // Contacts - use team-filtered endpoint
            endpoint = API_ENDPOINTS.GET_TEAM_CONTACTS;
          }

          const data = await request(endpoint, { teamId });

          // Store raw data
          setRawData(data || []);

        } catch (err) {
          console.error('Error fetching data:', err);
          setError(err instanceof Error ? err.message : 'Failed to fetch data');
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }, [entityType, schema, field]);

    // Create columns when rawData or searchQuery changes (client-side filtering)
    useEffect(() => {
      if (!rawData.length) {
        setColumns([]);
        return;
      }

      // Get field options
      let fieldOptions: string[] = field.options ?
        field.options
          .map((opt: any) => {
            if (typeof opt === 'string') {
              return opt.trim() || null; // Return null for empty strings
            } else if (typeof opt === 'object' && opt !== null) {
              // Handle option objects with value/label properties
              const value = opt.value || opt.label || '';
              const result = String(value).trim();
              return result || null; // Return null for empty results
            } else {
              const result = String(opt).trim();
              return result || null; // Return null for empty results
            }
          })
          .filter((opt: string | null): opt is string => opt !== null && opt !== '') // Type guard to ensure only strings
        : [];

      // Apply lane visibility filter if specified
      if (visibleLanes.length > 0) {
        fieldOptions = fieldOptions.filter(opt => visibleLanes.includes(opt));
      }

      // Create columns with filtered data (using filteredData which has record filters applied)
      const newColumns = createColumns(fieldOptions, filteredData);

      // Filter out unassigned column if not included
      const finalColumns = includeUnassigned
        ? newColumns
        : newColumns.filter(col => col.id !== 'unassigned');

      setColumns(finalColumns);
    }, [filteredData, searchQuery, field, visibleLanes, includeUnassigned]);

    // Native HTML5 drag and drop handlers
    const handleDragStart = (id: string) => {
      setDraggingId(id);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>, columnId: string) => {
      e.preventDefault(); // Allow dropping
      setHoveredColumnId(columnId);
    };

    const handleDragLeave = () => {
      setHoveredColumnId(null);
    };

    const handleDrop = async (columnId: string) => {
      if (!draggingId) {
        setHoveredColumnId(null);
        return;
      }

      // Find the item being dragged
      const activeItem = findItemById(draggingId);
      if (!activeItem || activeItem.columnId === columnId) {
        setDraggingId(null);
        setHoveredColumnId(null);
        return;
      }

      const originalColumnId = activeItem.columnId;

      try {
        // Optimistic update: Update local state first for instant feedback
        setColumns(prevColumns => {
          const newColumns = [...prevColumns];

          // Remove item from source column
          const sourceColumn = newColumns.find(col => col.id === originalColumnId);
          if (sourceColumn) {
            sourceColumn.items = sourceColumn.items.filter(item => item.id !== draggingId);
          }

          // Add item to target column
          const targetColumn = newColumns.find(col => col.id === columnId);
          if (targetColumn) {
            const updatedItem = { ...activeItem, columnId: columnId };
            targetColumn.items.push(updatedItem);
          }

          return newColumns;
        });

        // Then update the database
        const newValue = columnId === 'unassigned' ? '' : columnId;
        const fieldUpdate = { [field.name]: newValue };

        if (entityType === 2) {
          // Update account - send field updates as JSON data and current name
          await updateAccount.mutateAsync({
            id: activeItem.data.id,
            name: activeItem.data.name || activeItem.title || '',
            data: JSON.stringify(fieldUpdate)
          });
        } else {
          // Update contact - send field updates as JSON data
          await updateContact.mutateAsync({
            id: activeItem.data.id,
            data: JSON.stringify(fieldUpdate)
          });
        }

        // Success - the optimistic update was correct, no need to change anything

        // Find column names for user feedback
        const sourceColumnName = columns.find(col => col.id === originalColumnId)?.title || 'Unknown';
        const targetColumnName = columns.find(col => col.id === columnId)?.title || 'Unknown';

        toast.success(`Moved "${activeItem.title}" from "${sourceColumnName}" to "${targetColumnName}"`);

      } catch (err) {
        console.error('Error updating item:', err);

        // Rollback optimistic update on failure
        setColumns(prevColumns => {
          const newColumns = [...prevColumns];

          // Remove item from target column
          const targetColumn = newColumns.find(col => col.id === columnId);
          if (targetColumn) {
            targetColumn.items = targetColumn.items.filter(item => item.id !== draggingId);
          }

          // Add item back to original column
          const sourceColumn = newColumns.find(col => col.id === originalColumnId);
          if (sourceColumn) {
            sourceColumn.items.push(activeItem);
          }

          return newColumns;
        });

        // Show error notification to user
        toast.error(`Failed to update ${entityType === 2 ? 'account' : 'contact'}. Please try again.`);
      }

      setDraggingId(null);
      setHoveredColumnId(null);
    };

    const findItemById = (id: string): KanbanItem | null => {
      for (const column of columns) {
        const item = column.items.find(item => item.id === id);
        if (item) return item;
      }
      return null;
    };

    // Expose export function to parent component
    useImperativeHandle(ref, () => ({
      exportBoard: () => {
        const boardTitle = boardName || 'Kanban Board';
        const groupingField = field.label || field.name;
        const exportDate = new Date().toLocaleString();
        const entityTypeName = entityType === 2 ? 'Accounts' : 'Contacts';
        const totalItems = columns.reduce((sum, col) => sum + col.items.length, 0);

        const htmlLines = [
          '<!DOCTYPE html>',
          '<html>',
          '  <head>',
          '    <meta charset="UTF-8">',
          '    <title>Kanban Board Export</title>',
          '    <style>',
          '      body {',
          '        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;',
          '        padding: 40px;',
          '        color: #333;',
          '      }',
          '      h1 {',
          '        font-size: 24px;',
          '        margin-bottom: 10px;',
          '        color: #1a1a1a;',
          '      }',
          '      .info {',
          '        margin-bottom: 30px;',
          '        padding: 16px;',
          '        background-color: #f9fafb;',
          '        border-radius: 8px;',
          '        border-left: 4px solid #2563eb;',
          '      }',
          '      .info-row {',
          '        display: flex;',
          '        margin-bottom: 8px;',
          '        font-size: 14px;',
          '      }',
          '      .info-row:last-child {',
          '        margin-bottom: 0;',
          '      }',
          '      .info-label {',
          '        font-weight: 600;',
          '        color: #4b5563;',
          '        min-width: 120px;',
          '      }',
          '      .info-value {',
          '        color: #1f2937;',
          '      }',
          '      .group {',
          '        margin-bottom: 30px;',
          '        page-break-inside: avoid;',
          '      }',
          '      .group-title {',
          '        font-size: 18px;',
          '        font-weight: 600;',
          '        margin-bottom: 12px;',
          '        color: #2563eb;',
          '        border-bottom: 2px solid #2563eb;',
          '        padding-bottom: 8px;',
          '      }',
          '      .item {',
          '        margin-left: 20px;',
          '        margin-bottom: 8px;',
          '        padding: 8px;',
          '        border-left: 3px solid #e5e7eb;',
          '      }',
          '      .item-title {',
          '        font-weight: 500;',
          '        color: #1a1a1a;',
          '      }',
          '      .item-subtitle {',
          '        font-size: 14px;',
          '        color: #6b7280;',
          '        margin-top: 4px;',
          '      }',
          '      @media print {',
          '        body {',
          '          padding: 20px;',
          '        }',
          '      }',
          '    </style>',
          '  </head>',
          '  <body>',
          `    <h1>${boardTitle}</h1>`,
          '    <div class="info">',
          '      <div class="info-row">',
          '        <span class="info-label">Entity Type:</span>',
          `        <span class="info-value">${entityTypeName}</span>`,
          '      </div>',
          '      <div class="info-row">',
          '        <span class="info-label">Schema:</span>',
          `        <span class="info-value">${schema.name}</span>`,
          '      </div>',
          '      <div class="info-row">',
          '        <span class="info-label">Grouped By:</span>',
          `        <span class="info-value">${groupingField}</span>`,
          '      </div>',
          '      <div class="info-row">',
          '        <span class="info-label">Total Items:</span>',
          `        <span class="info-value">${totalItems}</span>`,
          '      </div>',
          '      <div class="info-row">',
          '        <span class="info-label">Exported:</span>',
          `        <span class="info-value">${exportDate}</span>`,
          '      </div>',
        ];

        // Add search query info if present
        if (searchQuery) {
          htmlLines.push('      <div class="info-row">');
          htmlLines.push('        <span class="info-label">Search Filter:</span>');
          htmlLines.push(`        <span class="info-value">${searchQuery}</span>`);
          htmlLines.push('      </div>');
        }

        htmlLines.push('    </div>');

        columns.forEach(column => {
          htmlLines.push('    <div class="group">');
          htmlLines.push(`      <div class="group-title">${column.title} (${column.items.length})</div>`);

          if (column.items.length > 0) {
            column.items.forEach(item => {
              htmlLines.push('      <div class="item">');
              htmlLines.push(`        <div class="item-title">${item.title}</div>`);
              if (item.subtitle) {
                htmlLines.push(`        <div class="item-subtitle">${item.subtitle}</div>`);
              }
              htmlLines.push('      </div>');
            });
          } else {
            htmlLines.push('      <div class="item">');
            htmlLines.push('        <div class="item-subtitle">No items</div>');
            htmlLines.push('      </div>');
          }

          htmlLines.push('    </div>');
        });

        htmlLines.push('  </body>');
        htmlLines.push('</html>');

        const html = htmlLines.join('\n');

        // Open new window and write content
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(html);
          printWindow.document.close();
          // Wait for content to load, then trigger print
          printWindow.onload = () => {
            printWindow.print();
          };
        } else {
          toast.error('Unable to open print window. Please check your popup blocker settings.');
        }
      }
    }), [columns, field, entityType, schema, searchQuery, boardName]);

    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Spinner />
          <span className={`ml-3 ${THEME_CLASSES.text.primary}`}>Loading {entityType === 2 ? 'accounts' : 'contacts'}...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className={`text-red-500 p-4 text-center`}>
          Error: {error}
        </div>
      );
    }

    return (
      <div className="flex h-full overflow-x-auto pb-6">
        {columns.map((column, index) => (
          <KanbanColumnComponent
            key={column.id}
            column={column}
            entityType={entityType}
            searchQuery={searchQuery}
            isFirst={index === 0}
            isLast={index === columns.length - 1}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            draggingId={draggingId}
            hoveredColumnId={hoveredColumnId}
          />
        ))}
      </div>
    );
  });

KanbanBoard.displayName = 'KanbanBoard';

export default KanbanBoard;
